import { useCallback } from 'react'
import { SigningDefinition, SigningRequest } from 'types'
import { RequestError, applySearchParametersToUrl, fetchWithTimeout, logFactory } from 'utils'
import { useHttpClientConfig } from '../useHttpClientConfig'
import { useServiceCountryUrl } from '../useServiceCountryUrl'

const log = logFactory('useDocumentSigningService')

const useDocumentSigningService = () => {
  const { url: serviceUrl } = useServiceCountryUrl('document-signing')
  const { getConfig } = useHttpClientConfig()

  const getServiceUrl = useCallback(() => serviceUrl.href, [serviceUrl])

  const makeRequest = useCallback(
    async <TResponse>(pathname: string, searchParams: Record<string, unknown>) => {
      const baseUrl = getServiceUrl()
      const url = new URL(pathname, baseUrl)
      const hydratedUrl = applySearchParametersToUrl(url, searchParams)
      const configInit = await getConfig()
      const response = await fetchWithTimeout(hydratedUrl.href, configInit)

      if (!response.ok) {
        throw new RequestError(`OPS request failed: ${response.status} ${response.statusText}`, response.status)
      }
      const json = await response.json()

      return (json?.data ?? json) as TResponse
    },
    [getServiceUrl, getConfig]
  )

  const getSigningDefinitions = useCallback(
    (args: { country: string }) => {
      const search = { country: args.country, limit: 100 }

      log('Fetching signing-definitions', search)

      return makeRequest<SigningDefinition[]>('/operations/v1/signing-definitions', search)
    },
    [makeRequest]
  )

  const getSigningRequests = useCallback(
    (args: { signingDefinitionGid: string[]; documentGid: string }) => {
      const search = {
        documentGid: args.documentGid,
        signingDefinitionGid: args.signingDefinitionGid.join(','),
      }

      log('Fetching signing-requests', search)

      return makeRequest<SigningRequest[]>('/operations/v1/signing-requests', search)
    },
    [makeRequest]
  )

  return {
    getSigningDefinitions,
    getSigningRequests,
  }
}

export { useDocumentSigningService }
